import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';

const ImportadorCompensaciones = ({ onDatosImportados, onClose, empleadosActivos = [] }) => {
  const [archivoCargado, setArchivoCargado] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [descargandoPlantilla, setDescargandoPlantilla] = useState(false);
  const [datosPreview, setDatosPreview] = useState([]);
  const [errores, setErrores] = useState([]);
  const [configuracion, setConfiguracion] = useState({
    columnaEmpleado: 'Empleado',
    columnaCargo: 'Cargo',
    columnaArea: 'Área de Trabajo',
    columnaProyecto: 'Proyecto',
    columnaHorasProximoMes: 'Total Próximo Mes',
    columnaEstado: 'Estado',
    columnaDiasLaborables: 'Días Laborables',
    columnaMinutosAcum: 'Minutos Acum',
    columnaTareosProcesados: 'Tareos Procesados'
  });

  // 📥 Descargar plantilla Excel con nombres de trabajadores activos
  const descargarPlantilla = useCallback(() => {
    if (!empleadosActivos || empleadosActivos.length === 0) {
      setErrores(['No hay empleados activos para generar la plantilla. Abra la planilla con empleados cargados.']);
      return;
    }
    setDescargandoPlantilla(true);
    setErrores([]);
    try {
      const encabezados = ['Empleado', 'Total Próximo Mes (h)', 'Cargo', 'Área', 'Proyecto', 'Estado'];
      const filas = empleadosActivos.map(emp => [
        emp.nombre || emp.nombre_completo || '',
        '', // Horas vacías para que el usuario las llene (debe ser > 0 para importar)
        emp.cargo || '',
        emp.areas_de_trabajo || '',
        emp.proyecto || '',
        'A FAVOR'
      ]);
      const datos = [encabezados, ...filas];
      const ws = XLSX.utils.aoa_to_sheet(datos);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Compensaciones');
      const fecha = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `plantilla_compensaciones_${fecha}.xlsx`);
    } catch (error) {
      console.error('Error generando plantilla:', error);
      setErrores(['Error al generar la plantilla: ' + (error.message || 'Error desconocido')]);
    } finally {
      setDescargandoPlantilla(false);
    }
  }, [empleadosActivos]);

  // 📂 Manejar selección de archivo
  const manejarSeleccionArchivo = useCallback((evento) => {
    const archivo = evento.target.files[0];
    if (!archivo) return;

    // Validar tipo de archivo
    const tiposPermitidos = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv' // .csv
    ];

    if (!tiposPermitidos.includes(archivo.type) && !archivo.name.match(/\.(xlsx|xls|csv)$/i)) {
      setErrores(['Formato de archivo no válido. Use Excel (.xlsx, .xls) o CSV (.csv)']);
      return;
    }

    setArchivoCargado(archivo);
    setErrores([]);
    procesarArchivo(archivo);
  }, []);

  // 🔄 Procesar archivo Excel/CSV
  const procesarArchivo = useCallback(async (archivo) => {
    setProcesando(true);
    setDatosPreview([]);
    setErrores([]);

    try {
      const datos = await leerArchivo(archivo);
      
      if (!datos || datos.length === 0) {
        throw new Error('El archivo está vacío o no contiene datos válidos');
      }

      // Validar estructura mínima
      const erroresValidacion = validarEstructuraDatos(datos);
      if (erroresValidacion.length > 0) {
        setErrores(erroresValidacion);
        setProcesando(false);
        return;
      }

      // Procesar y limpiar datos
      const datosLimpios = procesarDatosCompensaciones(datos);
      
      setDatosPreview(datosLimpios); // Mostrar TODA la lista de registros válidos (horas > 0)
      
    } catch (error) {
      console.error('Error procesando archivo:', error);
      setErrores([error.message || 'Error procesando el archivo']);
    } finally {
      setProcesando(false);
    }
  }, [configuracion]);

  // 📖 Leer archivo (Excel o CSV)
  const leerArchivo = (archivo) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          let datos;
          
          if (archivo.name.toLowerCase().endsWith('.csv')) {
            // Procesar CSV
            const texto = e.target.result;
            datos = procesarCSV(texto);
          } else {
            // Procesar Excel
            const datos_binarios = e.target.result;
            const workbook = XLSX.read(datos_binarios, { type: 'binary' });
            const primera_hoja = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[primera_hoja];
            datos = XLSX.utils.sheet_to_json(worksheet);
          }
          
          resolve(datos);
        } catch (error) {
          reject(new Error('Error leyendo el archivo: ' + error.message));
        }
      };
      
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      
      if (archivo.name.toLowerCase().endsWith('.csv')) {
        reader.readAsText(archivo);
      } else {
        reader.readAsBinaryString(archivo);
      }
    });
  };

  // 📄 Procesar CSV manualmente
  const procesarCSV = (texto) => {
    const lineas = texto.split('\n').map(linea => linea.trim()).filter(linea => linea);
    if (lineas.length < 2) throw new Error('CSV debe tener al menos encabezados y una fila de datos');
    
    const encabezados = lineas[0].split(',').map(h => h.replace(/"/g, '').trim());
    const datos = [];
    
    for (let i = 1; i < lineas.length; i++) {
      const valores = lineas[i].split(',').map(v => v.replace(/"/g, '').trim());
      const fila = {};
      
      encabezados.forEach((encabezado, index) => {
        fila[encabezado] = valores[index] || '';
      });
      
      datos.push(fila);
    }
    
    return datos;
  };

  // ✅ Validar estructura de datos
  const validarEstructuraDatos = (datos) => {
    const errores = [];
    
    if (!Array.isArray(datos) || datos.length === 0) {
      errores.push('No se encontraron datos en el archivo');
      return errores;
    }

    const primerFila = datos[0];
    const columnasRequeridas = [
      configuracion.columnaEmpleado,
      configuracion.columnaHorasProximoMes
    ];

    // Verificar columnas mínimas requeridas
    const columnasDisponibles = Object.keys(primerFila);
    const columnasFaltantes = columnasRequeridas.filter(col => 
      !columnasDisponibles.some(disponible => 
        disponible.toLowerCase().includes(col.toLowerCase()) ||
        col.toLowerCase().includes(disponible.toLowerCase())
      )
    );

    if (columnasFaltantes.length > 0) {
      errores.push(`Faltan columnas requeridas: ${columnasFaltantes.join(', ')}`);
    }

    return errores;
  };

  // 🔄 Procesar datos de compensaciones
  // ✅ FILTRO: Solo acepta registros con horas > 0 (positivas)
  // ✅ VISTA: Muestra TODA la lista de registros válidos, no limitado a 10
  const procesarDatosCompensaciones = (datosRaw) => {
    return datosRaw.map((fila, index) => {
      try {
        // Buscar columnas con nombres flexibles
        const empleado = buscarValorColumna(fila, [configuracion.columnaEmpleado, 'empleado', 'nombre', 'trabajador']);
        const cargo = buscarValorColumna(fila, [configuracion.columnaCargo, 'cargo', 'puesto', 'position']);
        const area = buscarValorColumna(fila, [configuracion.columnaArea, 'area', 'área', 'department']);
        const proyecto = buscarValorColumna(fila, [configuracion.columnaProyecto, 'proyecto', 'project']);
        const horasProximoMes = buscarValorColumna(fila, [configuracion.columnaHorasProximoMes, 'total próximo mes', 'horas', 'hours']);
        const estado = buscarValorColumna(fila, [configuracion.columnaEstado, 'estado', 'status']);
        const diasLaborables = buscarValorColumna(fila, [configuracion.columnaDiasLaborables, 'días laborables', 'dias']);
        const minutosAcum = buscarValorColumna(fila, [configuracion.columnaMinutosAcum, 'minutos acum', 'minutos']);
        const tareosProcesados = buscarValorColumna(fila, [configuracion.columnaTareosProcesados, 'tareos procesados', 'tareos']);

        return {
          fila: index + 1,
          empleado: empleado || '',
          cargo: cargo || '',
          areaTrabjao: area || '',
          proyecto: proyecto || '',
          horasProximoMes: parseFloat(horasProximoMes) || 0,
          estado: estado || 'A FAVOR',
          diasLaborables: parseInt(diasLaborables) || 0,
          minutosAcum: parseInt(minutosAcum) || 0,
          tareosProcesados: parseInt(tareosProcesados) || 0,
          // Calcular datos adicionales
          horasCalculadas: calcularHorasDesdeMinutos(parseInt(minutosAcum) || 0),
          esDatoValido: empleado.trim() !== '' && parseFloat(horasProximoMes) > 0 // Solo acepta empleados con horas > 0
        };
      } catch (error) {
        console.error(`Error procesando fila ${index + 1}:`, error);
        return {
          fila: index + 1,
          empleado: '',
          error: error.message,
          esDatoValido: false
        };
      }
    }).filter(item => item.esDatoValido);
  };

  // 🔍 Buscar valor en columna con nombres flexibles
  const buscarValorColumna = (fila, posiblesNombres) => {
    for (let nombre of posiblesNombres) {
      const columnaEncontrada = Object.keys(fila).find(key => 
        key.toLowerCase().includes(nombre.toLowerCase()) ||
        nombre.toLowerCase().includes(key.toLowerCase())
      );
      
      if (columnaEncontrada && fila[columnaEncontrada] !== undefined) {
        return String(fila[columnaEncontrada]).trim();
      }
    }
    return '';
  };

  // ⏰ Calcular horas desde minutos
  const calcularHorasDesdeMinutos = (minutos) => {
    const horas = Math.floor(minutos / 60);
    const minutosRestantes = minutos % 60;
    return `${horas}:${minutosRestantes.toString().padStart(2, '0')}`;
  };

  // ✅ Confirmar importación
  const confirmarImportacion = async () => {
    if (!archivoCargado || datosPreview.length === 0) return;

    setProcesando(true);
    
    try {
      const todosDatos = await leerArchivo(archivoCargado);
      const datosFinales = procesarDatosCompensaciones(todosDatos);
      
      const totalHoras = datosFinales.reduce((sum, item) => sum + item.horasProximoMes, 0);
      const empleadosUnicos = [...new Set(datosFinales.map(item => item.empleado))].length;
      
      const resumen = {
        totalRegistros: datosFinales.length,
        totalHoras: totalHoras,
        empleadosUnicos: empleadosUnicos,
        areasUnicas: [...new Set(datosFinales.map(item => item.areaTrabjao).filter(Boolean))].length,
        proyectosUnicos: [...new Set(datosFinales.map(item => item.proyecto).filter(Boolean))].length,
        promedioHorasPorEmpleado: empleadosUnicos > 0 ? (totalHoras / empleadosUnicos).toFixed(2) : 0
      };

      onDatosImportados({
        datos: datosFinales,
        resumen,
        archivoOriginal: archivoCargado.name,
        fechaImportacion: new Date().toISOString()
      });

    } catch (error) {
      setErrores([error.message || 'Error procesando los datos finales']);
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">📥 Importador de Compensaciones</h3>
              <p className="text-sm text-gray-600">Cargar datos de empleados desde Excel o CSV</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Descargar plantilla con trabajadores activos */}
        {empleadosActivos && empleadosActivos.length > 0 && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800 mb-2">
              📋 Plantilla con <strong>{empleadosActivos.length}</strong> trabajadores activos. Descárguela, complete las horas y opcionales, luego cárguela aquí.
            </p>
            <button
              type="button"
              onClick={descargarPlantilla}
              disabled={descargandoPlantilla}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {descargandoPlantilla ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Generando...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Descargar plantilla
                </>
              )}
            </button>
          </div>
        )}

        {empleadosActivos && empleadosActivos.length === 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            ℹ️ No hay empleados activos en esta planilla. La plantilla no está disponible; puede usar un archivo con columnas Empleado y Total Próximo Mes (h).
          </div>
        )}

        {/* Área de carga de archivo */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seleccionar archivo
          </label>
          <div className="relative">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={manejarSeleccionArchivo}
              className="hidden"
              id="archivo-compensaciones"
            />
            <label
              htmlFor="archivo-compensaciones"
              className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <svg className="w-8 h-8 mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Clic para cargar</span> o arrastra el archivo
                </p>
                <p className="text-xs text-gray-500">Excel (.xlsx, .xls) o CSV (.csv)</p>
              </div>
            </label>
          </div>
          
          {archivoCargado && (
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm text-blue-700">
                📄 <strong>{archivoCargado.name}</strong> ({(archivoCargado.size / 1024).toFixed(1)} KB)
              </p>
            </div>
          )}
        </div>

        {/* Errores */}
        {errores.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h4 className="font-semibold text-red-800 mb-2">❌ Errores encontrados:</h4>
            <ul className="list-disc list-inside text-sm text-red-700">
              {errores.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Indicador de procesamiento */}
        {procesando && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="text-blue-800">🔄 Procesando archivo...</span>
            </div>
          </div>
        )}

        {/* Preview de datos */}
        {datosPreview.length > 0 && (
          <div className="mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">👀 Registros válidos ({datosPreview.length} empleados con horas &gt; 0)</h4>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empleado</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cargo</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Área</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Horas Próximo Mes</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {datosPreview.map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-2 text-sm text-gray-900">{item.empleado}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{item.cargo}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{item.areaTrabjao}</td>
                      <td className="px-4 py-2 text-sm font-medium text-blue-600">{item.horasProximoMes}h</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          item.estado === 'A FAVOR' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.estado}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-700">
                ✅ <strong>{datosPreview.length}</strong> registros válidos encontrados (solo empleados con horas &gt; 0)
              </p>
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancelar
          </button>
          
          <button
            onClick={confirmarImportacion}
            disabled={!archivoCargado || datosPreview.length === 0 || procesando || errores.length > 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {procesando ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Procesando...</span>
              </div>
            ) : (
              <>📥 Importar Datos</>
            )}
          </button>
        </div>

        {/* Información adicional */}
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <h5 className="font-semibold text-yellow-800 mb-1">💡 Formato esperado del archivo:</h5>
          <ul className="text-xs text-yellow-700 list-disc list-inside">
            <li><strong>Empleado:</strong> Nombre completo del trabajador</li>
            <li><strong>Total Próximo Mes (h):</strong> Horas de compensación (debe ser > 0)</li>
            <li><strong>Cargo, Área, Proyecto:</strong> Información adicional (opcional)</li>
            <li><strong>Estado:</strong> A FAVOR, EN CONTRA, etc. (opcional)</li>
            <li><strong>⚠️ Nota:</strong> Solo se importarán empleados con horas > 0</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ImportadorCompensaciones;
